import React, { useEffect, useState } from 'react';
import { apiRequest, getApiUrl } from '../../config/api';
import LegalStepper from './LegalStepper';
import TenantTypeSelector from './TenantTypeSelector';
import TenantProfileForm from './TenantProfileForm';
import LegalDocumentViewer from './LegalDocumentViewer';
import './legal.css';

function LegalOnboardingFlow({ botId, onComplete, reanudarPerfilId, reanudarTipo }) {
  const [step, setStep] = useState('type');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tenantTypes, setTenantTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [profileId, setProfileId] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [acceptances, setAcceptances] = useState({});
  const [waiverOfWithdrawal, setWaiverOfWithdrawal] = useState(false);
  const [deliveryEmail, setDeliveryEmail] = useState('');

  useEffect(() => {
    if (botId == null) return undefined;
    let cancelado = false;
    setAcceptances({});
    setWaiverOfWithdrawal(false);
    setError('');
    setLoading(false);

    if (reanudarPerfilId != null) {
      setSelectedType(reanudarTipo || null);
      setProfileId(reanudarPerfilId);
      setStep('documents');
      apiRequest(getApiUrl(`/legal/onboarding/documents?botId=${botId}`))
        .then((res) => {
          if (!res.ok) throw new Error('documentos');
          return res.json();
        })
        .then((docs) => {
          if (cancelado) return;
          const pendientes = docs || [];
          setDocuments(pendientes);
          if (pendientes.length === 0) {
            setError('No hay documentos que firmar para este tipo de cuenta');
          }
        })
        .catch(() => {
          if (!cancelado) setError('Error cargando los documentos');
        });
      return () => {
        cancelado = true;
      };
    }

    setStep('type');
    setSelectedType(null);
    setProfileId(null);
    setDocuments([]);
    setDeliveryEmail('');
    apiRequest(getApiUrl(`/legal/onboarding/tenant-types?botId=${botId}`))
      .then((res) => {
        if (!res.ok) throw new Error('tipos');
        return res.json();
      })
      .then((tipos) => {
        if (!cancelado) setTenantTypes(tipos);
      })
      .catch(() => {
        if (!cancelado) setError('Error cargando tipos de tenant');
      });
    return () => {
      cancelado = true;
    };
  }, [botId, reanudarPerfilId, reanudarTipo]);

  const irAPaso = (siguiente) => {
    if (reanudarPerfilId != null) return;
    const orden = ['type', 'profile', 'documents'];
    if (orden.indexOf(siguiente) <= orden.indexOf(step)) {
      setStep(siguiente);
    }
  };

  const handleTypeConfirm = () => {
    if (selectedType) setStep('profile');
  };

  const handleProfileSubmit = async (formData) => {
    setLoading(true);
    setError('');
    try {
      const res = await apiRequest(getApiUrl('/legal/onboarding/profile'), {
        method: 'POST',
        body: JSON.stringify({ ...formData, botId }),
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.errors?.join(', ') || data.error || 'Error guardando datos');
        return;
      }
      const { profileId: id } = await res.json();
      setProfileId(id);
      setDeliveryEmail(formData.email);
      const docsRes = await apiRequest(getApiUrl(`/legal/onboarding/documents?botId=${botId}`));
      if (!docsRes.ok) {
        setError('Error cargando los documentos');
        return;
      }
      const docs = await docsRes.json();
      if (!Array.isArray(docs) || docs.length === 0) {
        setError('No hay documentos que firmar para este tipo de cuenta');
        return;
      }
      setDocuments(docs);
      setStep('documents');
    } catch (err) {
      setError('Error de red');
    } finally {
      setLoading(false);
    }
  };

  const allDocumentsAccepted = () =>
    documents.length > 0 && documents.every((doc) => acceptances[doc.versionId]);

  const handleFinalSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiRequest(getApiUrl('/legal/onboarding/accept'), {
        method: 'POST',
        body: JSON.stringify({
          botId,
          profileId,
          deliveryEmail,
          waiverOfWithdrawal: selectedType === 'PERSONA_FISICA' ? waiverOfWithdrawal : null,
          acceptances: documents.map((doc) => ({
            documentVersionId: doc.versionId,
            accepted: true,
            checkboxText: doc.checkboxText,
          })),
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.error || 'Error registrando aceptación');
        return;
      }
      const data = await res.json();
      if (onComplete) onComplete(data.emailSent !== false);
    } catch (err) {
      setError('Error de red');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="legal-mesa legal-onboarding-flow">
      <LegalStepper paso={step} onPaso={irAPaso} />
      {error && <div className="legal-banner-error">{error}</div>}
      {tenantTypes.length === 0 && !error && step === 'type' ? (
        <p className="legal-muted">Cargando tipos de cuenta…</p>
      ) : null}

      {step === 'type' && (
        <>
          <TenantTypeSelector
            tenantTypes={tenantTypes}
            selectedType={selectedType}
            onSelect={setSelectedType}
          />
          <button
            type="button"
            className="legal-btn-primary"
            onClick={handleTypeConfirm}
            disabled={!selectedType}
          >
            Continuar
          </button>
        </>
      )}

      {step === 'profile' && (
        <TenantProfileForm
          tenantType={selectedType}
          onSubmit={handleProfileSubmit}
          loading={loading}
        />
      )}

      {step === 'documents' && (
        <div className="legal-lectura">
          <p className="legal-lectura-intro">Lee cada documento hasta el final antes de aceptarlo.</p>
          {documents.map((doc, index) => (
            <LegalDocumentViewer
              key={doc.versionId}
              document={doc}
              accepted={acceptances[doc.versionId] || false}
              onAcceptChange={(accepted) => setAcceptances((prev) => ({ ...prev, [doc.versionId]: accepted }))}
              waiverOfWithdrawal={waiverOfWithdrawal}
              onWaiverChange={setWaiverOfWithdrawal}
              showWaiverOption={selectedType === 'PERSONA_FISICA' && index === documents.length - 1}
            />
          ))}
          <div className="legal-campo legal-copia-email">
            <label htmlFor="legal-delivery-email">Email para la copia</label>
            <input
              id="legal-delivery-email"
              type="email"
              value={deliveryEmail}
              onChange={(e) => setDeliveryEmail(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="legal-btn-primary"
            onClick={handleFinalSubmit}
            disabled={!allDocumentsAccepted() || loading}
          >
            {loading ? 'Procesando…' : 'Firmar y enviar'}
          </button>
        </div>
      )}
    </div>
  );
}

export default LegalOnboardingFlow;
